import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";

import type {
  LanguageDto,
  ProjectConnectionDetailsDto,
  UserRoleTypeCommunityGroupPermissionsEntityRequest,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { Form } from "@/components/Form/Form";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormGroupIconPicker } from "@/components/Form/FormGroupIconPicker";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadio, FormRadioGroup } from "@/components/Form/FormRadio";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { GroupIconType } from "@/components/GroupIcon/GroupIcon";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PermissionSelector } from "@/components/PermissionSelector/PermissionSelector";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { getFlagIcon } from "@/helpers/flags";
import { createImageSizeRule, createImageTypeRule, createRequiredStringRule } from "@/helpers/rules";
import type { FormPermissions } from "@/helpers/system-groups/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface ProjectConnectionGroupFormProps {
  connectionId: string;
  groupId?: string;
  isEditMode: boolean;
  languageId: LanguageDto["id"];
  projectConnection: ProjectConnectionDetailsDto;
  defaultValues: CreateOrEditProjectConnectionGroupFormValues;
  onSubmit: (data: CreateOrEditProjectConnectionGroupFormValues) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
  isSubmitting: boolean;
}

const MAX_LENGTH_NAME = 40;
const MAX_LENGTH_DESCRIPTION = 400;

export function ProjectConnectionGroupForm({
  connectionId,
  groupId,
  isEditMode,
  languageId,
  projectConnection,
  defaultValues,
  onSubmit,
  onDelete,
  isSubmitting,
}: ProjectConnectionGroupFormProps): React.ReactNode {
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<{
    cid: string;
    gid: string;
  }>("pc-group-delete-modal");
  const formMethods = useForm<CreateOrEditProjectConnectionGroupFormValues>({
    defaultValues,
  });
  const postingLevel = useWatch({
    name: "postingLevel",
    control: formMethods.control,
  });
  const permissions = useWatch({
    name: "permissions",
    control: formMethods.control,
  });

  const audience = useMemo(() => {
    if (postingLevel === "project") {
      return ["project"];
    } else if (postingLevel === "projectConnection") {
      return ["projectConnection"];
    } else {
      return ["project", "projectConnection"];
    }
  }, [postingLevel]);
  useEffect(() => {
    if (postingLevel === "project") {
      formMethods.setValue("audience", "project");
    } else if (postingLevel === "projectConnection") {
      formMethods.setValue("audience", "projectConnection");
    }
  }, [formMethods, postingLevel]);

  const title = isEditMode
    ? t("page.project-connection-groups.edit.title")
    : t("page.project-connection-groups.create.title");

  const flagIcon = getFlagIcon({
    languageId,
    width: 16,
  });

  const permissionFields = [
    { key: "canCreatePost", label: t("page.project-connection-groups.form.permissions.can-create-post") },
    {
      key: "canCreateAnnouncement",
      label: t("page.project-connection-groups.form.permissions.can-create-announcement"),
    },
    { key: "canListPosts", label: t("page.project-connection-groups.form.permissions.can-list-posts") },
    { key: "canCommentPost", label: t("page.project-connection-groups.form.permissions.can-comment") },
  ];

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.project-connections.title"),
              to: routes.projectConnections.list(),
            },
            {
              name: projectConnection.name,
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
        <Form.Container>
          <Form.Body>
            <Notice
              icon={<Translate01Icon />}
              variant="special"
              message={
                isEditMode
                  ? t("common.label.you-are-editing-in-original-content-language")
                  : t("common.label.your-input-will-be-auto-translated")
              }
            />
            <FormField label={t("page.project-connection-groups.form.type.label")}>
              <FormErrorWrapper<CreateOrEditProjectConnectionGroupFormValues> name="type">
                <FormRadioGroup>
                  <FormRadio<CreateOrEditProjectConnectionGroupFormValues>
                    name="type"
                    value="interest"
                    label={t("page.project-connection-groups.form.type.option.interest-group")}
                    rules={{
                      validate: {
                        required: createRequiredStringRule(t, "page.project-connection-groups.form.type.label"),
                      },
                    }}
                    disabled={isEditMode}
                  />
                  <FormRadio<CreateOrEditProjectConnectionGroupFormValues>
                    name="type"
                    value="helpCategory"
                    label={t("page.project-connection-groups.form.type.option.help-category")}
                    rules={{
                      validate: {
                        required: createRequiredStringRule(t, "page.project-connection-groups.form.type.label"),
                      },
                    }}
                    disabled={isEditMode}
                  />
                </FormRadioGroup>
              </FormErrorWrapper>
            </FormField>
            <FormField label={t("page.project-connection-groups.form.name.label")} actions={flagIcon} required>
              <FormInput<CreateOrEditProjectConnectionGroupFormValues>
                name="name"
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.project-connection-groups.form.name.label"),
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: MAX_LENGTH_NAME,
                    }),
                    value: MAX_LENGTH_NAME,
                  },
                }}
                placeholder={t("page.project-connection-groups.form.name.placeholder")}
              />
            </FormField>
            <FormField label={t("page.project-connection-groups.form.description.label")} actions={flagIcon} required>
              <FormTextArea<CreateOrEditProjectConnectionGroupFormValues>
                name="description"
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.project-connection-groups.form.description.label"),
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: MAX_LENGTH_DESCRIPTION,
                    }),
                    value: MAX_LENGTH_DESCRIPTION,
                  },
                }}
                placeholder={t("page.project-connection-groups.form.description.placeholder")}
                compactHeight
              />
            </FormField>

            <FormField label={t("page.project-connection-groups.form.icon.label")} required>
              <FormGroupIconPicker<CreateOrEditProjectConnectionGroupFormValues>
                data-testid="group-icon"
                id="icon"
                name="icon"
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.project-connection-groups.form.icon.label"),
                  }),
                }}
              />
            </FormField>
            <FormField label={t("page.project-connection-groups.form.image.label")} required>
              <FormImageInput<CreateOrEditProjectConnectionGroupFormValues, "image">
                name="image"
                id="imageFile"
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.project-connection-groups.form.image.label"),
                  }),
                  validate: {
                    size: createImageSizeRule({
                      t,
                    }),
                    type: createImageTypeRule({
                      t,
                    }),
                  },
                }}
              />
            </FormField>
            <FormField
              label={t("page.project-connection-groups.form.posting-level.label")}
              tooltip={t("page.project-connection-groups.form.posting-level.tooltip")}
            >
              <FormSelect<CreateOrEditProjectConnectionGroupFormValues, string>
                name="postingLevel"
                placeholder={t("page.project-connection-groups.form.posting-level.placeholder")}
                items={["projectConnection", "projectAndProjectConnection"]}
                keySelector={(x) => x}
                renderOption={(x) => {
                  switch (x) {
                    case "projectConnection":
                      return t("page.project-connection-groups.form.posting-level.options.connection");
                    case "projectAndProjectConnection":
                      return t("page.project-connection-groups.form.posting-level.options.both");
                  }
                }}
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.project-connection-groups.form.posting-level.label"),
                  }),
                }}
                disabled={isEditMode}
              />
            </FormField>
            <FormField
              label={t("page.project-connection-groups.form.audience.label")}
              tooltip={t("page.project-connection-groups.form.audience.tooltip")}
            >
              <FormSelect<CreateOrEditProjectConnectionGroupFormValues, string>
                name="audience"
                placeholder={t("page.project-connection-groups.form.audience.placeholder")}
                items={audience}
                keySelector={(x) => x}
                renderOption={(x) => {
                  switch (x) {
                    case "project":
                      return t("page.project-connection-groups.form.audience.options.project");
                    case "projectConnection":
                      return t("page.project-connection-groups.form.audience.options.connection");
                  }
                }}
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.project-connection-groups.form.audience.label"),
                  }),
                }}
                disabled={isEditMode && postingLevel !== "projectAndProjectConnection"}
              />
            </FormField>
            <FormField
              label={t("component.permission-section.title")}
              description={t("component.permission-section.description", {
                permissionTarget: t("common.entity.group", { count: 1 }).toLowerCase(),
              })}
            >
              <PermissionSelector
                defaultPermissions={permissions}
                fields={permissionFields}
                onSave={(newPermissions) => formMethods.setValue("permissions", newPermissions)}
              />
            </FormField>
            {isEditMode && groupId && sessionUser.isSuperAdmin && (
              <section className="flex w-full flex-col items-start gap-space-sm rounded-sm bg-background-danger-secondary p-card-compact">
                <span className="text-caption-emphasis text-text-danger">
                  {t("page.project-connection-groups.form.danger-zone")}
                </span>
                <footer className="flex w-full items-center justify-between gap-space-sm">
                  <p className="text-body">{t("page.project-connection-groups.form.danger-zone.description")}</p>
                  <Button
                    variant="filled"
                    tone="error"
                    onClick={() =>
                      openDeleteModal({
                        cid: connectionId,
                        gid: groupId,
                      })
                    }
                  >
                    {t("common.action.delete")}
                  </Button>
                </footer>
              </section>
            )}
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button type="submit" isLoading={isSubmitting}>
                {isEditMode ? t("common.action.edit") : t("common.action.create")}
              </Button>
            )}
          />
        </Form.Container>
      </Form.Root>
      <DeleteModal
        title={t("page.project-connection-groups.modal.delete.title")}
        description={t("page.project-connection-groups.modal.delete.description")}
        onDelete={() => onDelete()}
        deleteBtnProps={{
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
    </DocumentPage>
  );
}
export interface CreateOrEditProjectConnectionGroupFormValues {
  type: "interest" | "helpCategory" | "realEstate";
  name: string;
  description: string;
  icon?: GroupIconType;
  image?: FormImage[];
  permissions: FormPermissions<UserRoleTypeCommunityGroupPermissionsEntityRequest>;
  postingLevel: "project" | "projectConnection" | "projectAndProjectConnection";
  audience: "project" | "projectConnection";
}
